import React, { useState, useCallback, useEffect, useRef } from 'react';
import apiService from '../services/apiService';
import { useToast } from '../components/contexts/ToastContext';
import usePagination from '../hooks/usePagination';
import useDebounce from '../hooks/useDebounce';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EntityModal from '../components/common/EntityModal';
import InputConDictado from '../components/common/InputConDictado';
import CapturaFoto from '../components/galeria/CapturaFoto';
import Navbar from '../components/layout/Navbar';
import GaleriaGrid from '../components/galeria/GaleriaGrid';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Página Mi Galería — hub central de fotos del usuario.
 * No usa usePaginatedEntity (grid de fotos, no tabla).
 *
 * @returns {JSX.Element}
 */
function MiGaleria() {
  const { showToast } = useToast();
  const { page, limit, totalItems, setTotal, handlePageChange, handleLimitChange } =
    usePagination({ mode: 'server', initialLimit: 20 });

  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, foto: null });
  const [editingFoto, setEditingFoto] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [viewingFoto, setViewingFoto] = useState(null);
  const [loadingView, setLoadingView] = useState(false);

  const viewingFotoRef = useRef(viewingFoto);
  viewingFotoRef.current = viewingFoto;

  useEffect(() => {
    return () => {
      if (viewingFotoRef.current?.blobUrl) {
        URL.revokeObjectURL(viewingFotoRef.current.blobUrl);
      }
    };
  }, []);

  const debouncedSearch = useDebounce(search, 400);

  const fetchFotos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const response = await apiService.getMisFotos(params);
      if (response.success) {
        setFotos(response.data || []);
        setTotal(response.pagination?.total || 0);
      }
    } catch (err) {
      showToast('Error al cargar fotos', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFotos();
  }, [fetchFotos]);


  // Cerrar lightbox con Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (viewingFoto) closeView();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [viewingFoto]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = (foto) => setConfirmDialog({ open: true, foto });
  const closeConfirm = () => setConfirmDialog({ open: false, foto: null });
  const confirmDelete = async () => {
    const foto = confirmDialog.foto;
    closeConfirm();
    try {
      const response = await apiService.deleteFoto(foto.id);
      if (response.success) {
        showToast('Foto eliminada', 'success');
        fetchFotos();
      } else {
        showToast(response.message || 'Error al eliminar', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error al eliminar foto', 'error');
    }
  };

  const handleEdit = (foto) => {
    setEditingFoto(foto);
  };

  const handleSaveEdit = async (formData) => {
    setSavingEdit(true);
    try {
      const response = await apiService.updateFotoDescripcion(
        editingFoto.id,
        formData.descripcion?.trim() || null
      );
      if (response.success) {
        showToast('Descripción actualizada', 'success');
        setEditingFoto(null);
        fetchFotos();
      } else {
        showToast(response.message || 'Error al guardar', 'error');
      }
    } catch {
      showToast('Error al guardar', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const closeEditModal = () => {
    setEditingFoto(null);
  };


  const handleView = async (foto) => {
    setLoadingView(true);
    try {
      const response = await apiService.downloadFoto(foto.id);
      if (!response.ok) throw new Error('Error al cargar imagen');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setViewingFoto({ foto, blobUrl });
    } catch {
      showToast('Error al cargar imagen completa', 'error');
    } finally {
      setLoadingView(false);
    }
  };

  const closeView = () => {
    if (viewingFoto?.blobUrl) URL.revokeObjectURL(viewingFoto.blobUrl);
    setViewingFoto(null);
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  };

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Mi Galería' }
        ]} />

        <div className="filters-panel">
          <div className="filters-panel-top">
            <div className={`search-box flex-1${search ? ' filter-active' : ''}`}>
              <i className="fas fa-search sb-icon-left"></i>
              <input
                type="text"
                placeholder="Buscar en mis fotos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="search-clear" onClick={() => setSearch('')} title="Limpiar búsqueda">
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            <div className="filters-panel-actions">
              <CapturaFoto onFotoSubida={fetchFotos} inline />
              <button
                className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={toggleViewMode}
                title={viewMode === 'grid' ? 'Cambiar a lista' : 'Cambiar a grilla'}
              >
                <i className={`fas ${viewMode === 'grid' ? 'fa-th' : 'fa-list'}`}></i>
              </button>
            </div>
          </div>
        </div>

        <GaleriaGrid
          fotos={fotos}
          loading={loading}
          viewMode={viewMode}
          onDelete={handleDelete}
          onView={handleView}
          onEdit={handleEdit}
        />

        {!loading && fotos.length > 0 && (
          <Pagination
            currentPage={page}
            totalItems={totalItems}
            itemsPerPage={limit}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleLimitChange}
            itemName="fotos"
            loading={loading}
          />
        )}

        <ConfirmDialog
          isOpen={confirmDialog.open}
          title="Eliminar foto"
          message={`¿Eliminar "${confirmDialog.foto?.descripcion || 'esta foto'}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          onConfirm={confirmDelete}
          onCancel={closeConfirm}
          variant="danger"
        />

        <EntityModal
          isOpen={!!editingFoto}
          onClose={closeEditModal}
          onSave={handleSaveEdit}
          title="Editar descripción"
          loading={savingEdit}
          fields={[{
            name: 'descripcion',
            label: 'Descripción',
            type: 'custom',
            renderField: (value, onChange, { disabled }) => (
              <InputConDictado value={value} onChange={onChange} disabled={disabled} autoFocus />
            )
          }]}
          initialData={{ descripcion: editingFoto?.descripcion || '' }}
        />

        {/* Lightbox fullscreen */}
        {loadingView && (
          <div className="lightbox-backdrop">
            <i className="fas fa-spinner fa-spin fa-2x lightbox-loading"></i>
          </div>
        )}
        {viewingFoto && (
          <div className="lightbox-backdrop" onClick={closeView}>
            <button className="modal-close-btn lightbox-close-btn" onClick={closeView}>
              <i className="fas fa-times"></i>
            </button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <div className="lightbox-image-wrap">
                <img
                  src={viewingFoto.blobUrl}
                  alt={viewingFoto.foto.descripcion || 'Foto'}
                />
              </div>
              <div className="lightbox-info">
                <div className="lightbox-desc">
                  <strong>{viewingFoto.foto.descripcion || 'Foto'}</strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default MiGaleria;
